import { useQuery } from '@tanstack/react-query';
import {
    Card, Col, DatePicker, Empty, Row, Select, Space,
} from 'antd';
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';
import type { Dayjs } from 'dayjs';
import {
    CSSProperties, useMemo, useState,
} from 'react';
import {
    Bar, Doughnut,
} from 'react-chartjs-2';

import {
    dashboardQueries, usersQueries,
} from '@/api';
import { UserRole } from '@/constants';
import { ProjectStats } from '@/types';

import { RoleGuard } from './RoleGuard';

const { RangePicker } = DatePicker;

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
);

const monthNames = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

// Отримує ключ у форматі "YYYY-MM" для групування по рік+місяць
const getYearMonthKey = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth();
    return `${year}-${month.toString().padStart(2, '0')}`;
};

// Генерує масив останніх N місяців
const getLastNMonths = (n: number): string[] => {
    const result: string[] = [];
    const now = new Date();

    for (let i = n - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        result.push(`${date.getFullYear()}-${date.getMonth().toString().padStart(2, '0')}`);
    }

    return result;
};

// Форматує ключ "YYYY-MM" в назву місяця
const formatMonthLabel = (yearMonthKey: string): string => {
    const [year, month] = yearMonthKey.split('-');
    const monthIndex = parseInt(month, 10);
    const currentYear = new Date().getFullYear();

    // Якщо рік не поточний — додаємо рік до назви
    if (parseInt(year, 10) !== currentYear) {
        return `${monthNames[monthIndex]} ${year}`;
    }

    return monthNames[monthIndex];
};

const calculateMonthlyStatsFromStats = (projects: ProjectStats[]) => {
    // Групуємо по рік+місяць
    const projectsByYearMonth: Record<string, number> = {};
    const scriptsByYearMonth: Record<string, number> = {};

    projects.forEach((project) => {
        const key = getYearMonthKey(project.created_at);
        projectsByYearMonth[key] = (projectsByYearMonth[key] || 0) + 1;

        if (project.scripts && project.scripts.length > 0) {
            project.scripts.forEach((script) => {
                const scriptKey = getYearMonthKey(script.created_at);
                scriptsByYearMonth[scriptKey] = (scriptsByYearMonth[scriptKey] || 0) + 1;
            });
        }
    });

    // Беремо останні 12 місяців
    const last12Months = getLastNMonths(12);

    // Знаходимо місяці з даними
    const allKeys = new Set([...Object.keys(projectsByYearMonth), ...Object.keys(scriptsByYearMonth)]);
    const keysWithData = last12Months.filter((key) => allKeys.has(key));

    // Визначаємо діапазон для відображення
    let displayMonths: string[];

    if (keysWithData.length <= 6) {
        // Показуємо тільки місяці з даними (або останні 6 якщо даних мало)
        const startIndex = Math.max(0, last12Months.indexOf(keysWithData[0] || last12Months[6]));
        displayMonths = last12Months.slice(startIndex);
    } else {
        displayMonths = last12Months;
    }

    const projectsByMonth = displayMonths.map((key) => projectsByYearMonth[key] || 0);
    const scriptsByMonth = displayMonths.map((key) => scriptsByYearMonth[key] || 0);
    const labels = displayMonths.map(formatMonthLabel);

    return {
        projectsByMonth,
        scriptsByMonth,
        labels,
    };
};

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'top' as const,
        },
    },
};

const chartStyle: CSSProperties = {
    height: '100%',
};

const chartContainerStyle: CSSProperties = {
    height: '300px',
};

type ScriptTypeFilter = 'all' | 'default' | 'cross-sale';

export const DashboardCharts = () => {
    const [scriptTypeFilter, setScriptTypeFilter] = useState<ScriptTypeFilter>('all');
    const [scriptWriterIdFilter, setScriptWriterIdFilter] = useState<string>('all');
    const [scriptWriterDateRange, setScriptWriterDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

    const statsParams = useMemo(() => {
        if (scriptTypeFilter === 'all') {
            return {};
        }

        return {
            script_type: scriptTypeFilter,
        };
    }, [scriptTypeFilter]);

    const scriptWriterStatsParams = useMemo(() => {
        const params: {
            user_id?: string;
            date_from?: string;
            date_to?: string;
        } = {};

        if (scriptWriterIdFilter !== 'all') {
            params.user_id = scriptWriterIdFilter;
        }

        const dateFrom = scriptWriterDateRange?.[0];
        const dateTo = scriptWriterDateRange?.[1];

        if (dateFrom) {
            params.date_from = dateFrom.format('YYYY-MM-DD');
        }

        if (dateTo) {
            params.date_to = dateTo.format('YYYY-MM-DD');
        }

        return params;
    }, [scriptWriterDateRange, scriptWriterIdFilter]);

    const { data: statsData, isFetching: isStatsLoading } = useQuery(dashboardQueries.getStats(statsParams));
    const { data: scriptWriterStats, isFetching: isScriptWriterStatsLoading } = useQuery(dashboardQueries.getScriptWriterStats(scriptWriterStatsParams));
    const { data: scriptWritersData, isFetching: isScriptWritersLoading } = useQuery(usersQueries.getAllUsers({
        per_page: 1000,
        current_page: 1,
        order_column: 'nickname',
        order_direction: 'asc',
    }));

    const monthlyStats = useMemo(() => {
        if (!statsData) {
            return {
                projectsByMonth: [],
                scriptsByMonth: [],
                labels: [],
            };
        }

        return calculateMonthlyStatsFromStats(statsData.projects);
    }, [statsData]);

    const usersChartData = useMemo(() => {
        if (!statsData?.users) {
            return {
                labels: ['Адміністратори', 'Скриптологи', 'Менеджери', 'Без ролі'],
                datasets: [
                    {
                        label: 'Кількість користувачів',
                        data: [0, 0, 0, 0],
                        backgroundColor: [
                            'rgba(255, 99, 132, 0.6)',
                            'rgba(54, 162, 235, 0.6)',
                            'rgba(255, 206, 86, 0.6)',
                            'rgba(153, 102, 255, 0.6)',
                        ],
                        borderColor: [
                            'rgba(255, 99, 132, 1)',
                            'rgba(54, 162, 235, 1)',
                            'rgba(255, 206, 86, 1)',
                            'rgba(153, 102, 255, 1)',
                        ],
                        borderWidth: 1,
                    },
                ],
            };
        }

        return {
            labels: ['Супер-адміністратори', 'Адміністратори', 'Скриптологи', 'Менеджери', 'Без ролі'],
            datasets: [
                {
                    label: 'Кількість користувачів',
                    data: [
                        statsData.users.super_admin,
                        statsData.users.admin,
                        statsData.users.script_writer,
                        statsData.users.manager,
                        statsData.users.no_role,
                    ],
                    backgroundColor: [
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(54, 162, 235, 0.6)',
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 206, 86, 0.6)',
                        'rgba(153, 102, 255, 0.6)',
                    ],
                    borderColor: [
                        'rgba(255, 99, 132, 1)',
                        'rgba(54, 162, 235, 1)',
                        'rgba(75, 192, 192, 1)',
                        'rgba(255, 206, 86, 1)',
                        'rgba(153, 102, 255, 1)',
                    ],
                    borderWidth: 1,
                },
            ],
        };
    }, [statsData]);

    const monthlyData = useMemo(() => ({
        labels: monthlyStats.labels,
        datasets: [
            {
                label: 'Проекти',
                data: monthlyStats.projectsByMonth,
                backgroundColor: 'rgba(54, 162, 235, 0.6)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
            },
            {
                label: 'Скрипти',
                data: monthlyStats.scriptsByMonth,
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1,
            },
        ],
    }), [monthlyStats]);

    const scriptWriterOptions = useMemo(() => {
        const scriptWriters = scriptWritersData?.items
            .filter((user) => user.role === UserRole.ScriptWriter)
            .map((user) => ({
                label: user.nickname,
                value: user.id,
            })) || [];

        return [
            {
                label: 'Всі',
                value: 'all',
            },
            ...scriptWriters,
        ];
    }, [scriptWritersData?.items]);

    const scriptWriterChartData = useMemo(() => ({
        labels: scriptWriterStats?.map((item) => item.nickname) || [],
        datasets: [
            {
                label: 'Створено',
                data: scriptWriterStats?.map((item) => item.created) || [],
                backgroundColor: 'rgba(54, 162, 235, 0.6)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
            },
            {
                label: 'Правок',
                data: scriptWriterStats?.map((item) => item.updated) || [],
                backgroundColor: 'rgba(255, 159, 64, 0.6)',
                borderColor: 'rgba(255, 159, 64, 1)',
                borderWidth: 1,
            },
        ],
    }), [scriptWriterStats]);

    const scriptWriterChartOptions = useMemo(() => ({
        ...chartOptions,
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                },
            },
        },
    }), []);

    return (
        <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
                <Card
                    title="Статистика за рік"
                    variant="borderless"
                    style={chartStyle}
                    loading={isStatsLoading}
                    extra={(
                        <Select
                            value={scriptTypeFilter}
                            onChange={setScriptTypeFilter}
                            style={{ width: 180 }}
                            options={[
                                {
                                    label: 'Всі скрипти',
                                    value: 'all',
                                },
                                {
                                    label: 'Основні',
                                    value: 'default',
                                },
                                {
                                    label: 'Крос-сейли',
                                    value: 'cross-sale',
                                },
                            ]}
                        />
                    )}
                >
                    <div style={chartContainerStyle}>
                        <Bar data={monthlyData} options={chartOptions} />
                    </div>
                </Card>
            </Col>
            <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                <Col xs={24} md={12}>
                    <Card title="Кількість користувачів" variant="borderless" style={chartStyle} loading={isStatsLoading}>
                        <div style={chartContainerStyle}>
                            <Doughnut data={usersChartData} options={chartOptions} />
                        </div>
                    </Card>
                </Col>
            </RoleGuard>
            <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                <Col xs={24}>
                    <Card title="Статистика скриптологів" variant="borderless" style={chartStyle} loading={isScriptWriterStatsLoading}>
                        <Space wrap size={12} style={{ marginBottom: 16 }}>
                            <RangePicker
                                value={scriptWriterDateRange}
                                onChange={setScriptWriterDateRange}
                                format="YYYY-MM-DD"
                            />
                            <Select
                                value={scriptWriterIdFilter}
                                onChange={setScriptWriterIdFilter}
                                style={{ width: 220 }}
                                options={scriptWriterOptions}
                                loading={isScriptWritersLoading}
                            />
                        </Space>
                        <div style={chartContainerStyle}>
                            {(!scriptWriterStats || scriptWriterStats.length === 0) && !isScriptWriterStatsLoading ? (
                                <Empty description="Немає даних" />
                            ) : (
                                <Bar data={scriptWriterChartData} options={scriptWriterChartOptions} />
                            )}
                        </div>
                    </Card>
                </Col>
            </RoleGuard>
        </Row>
    );
};
